import React, { useState, useEffect } from 'react';
import { usersApi, blogsApi } from '../services/api';
import { Plus, Edit2, Trash2, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import './AdminUsers.css';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningUser, setAssigningUser] = useState(null);

  const [form, setForm] = useState({
    email: '',
    name: '',
    password: '',
    role: 'user'
  });

  const [emailError, setEmailError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setForm(f => ({ ...f, email: value }));
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const [assignForm, setAssignForm] = useState({
    blogId: '',
    role: 'user'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, blogsRes] = await Promise.all([
        usersApi.list(),
        blogsApi.list()
      ]);
      setUsers(usersRes.data);
      setBlogs(blogsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(form.email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      if (editingUser) {
        // Filter out empty password when editing
        const updateData = { ...form };
        if (!updateData.password) {
          delete updateData.password;
        }
        await usersApi.update(editingUser.id, updateData);
        toast.success('User updated');
      } else {
        await usersApi.create(form);
        toast.success('User created');
      }
      setShowModal(false);
      setEditingUser(null);
      setForm({ email: '', name: '', password: '', role: 'user' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role
    });
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    if (!confirm(`Are you sure you want to delete ${user.name}?`)) return;

    try {
      await usersApi.delete(user.id);
      toast.success('User deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleAssign = (user) => {
    setAssigningUser(user);
    setAssignForm({ blogId: '', role: 'user' });
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();

    try {
      await usersApi.assignToBlog(assigningUser.id, assignForm.blogId, assignForm.role);
      toast.success('User assigned to blog');
      setShowAssignModal(false);
      setAssigningUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to assign user');
    }
  };

  const handleRemoveFromBlog = async (userId, blogId) => {
    if (!confirm('Remove user from this blog?')) return;

    try {
      await usersApi.removeFromBlog(userId, blogId);
      toast.success('User removed from blog');
      loadData();
    } catch (error) {
      toast.error('Failed to remove user from blog');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner" />
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="admin-users">
      <div className="admin-header">
        <h1>User Management</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={20} />
          Add User
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Blogs</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>
                  <span className={`role-badge ${user.role}`}>
                    {user.role === 'super_admin' ? 'Super Admin' : 'User'}
                  </span>
                </td>
                <td>
                  <div className="user-blogs">
                    {user.blogs?.map(blog => (
                      <span key={blog.id} className="blog-tag">
                        {blog.blog_id} - {blog.domain} ({blog.blog_role})
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveFromBlog(user.id, blog.id)}
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                    {(!user.blogs || user.blogs.length === 0) && (
                      <span className="no-blogs">No blogs assigned</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-icon"
                      onClick={() => handleAssign(user)}
                      title="Assign to blog"
                    >
                      <UserPlus size={16} />
                    </button>
                    <button
                      className="btn btn-icon"
                      onClick={() => handleEdit(user)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn btn-icon btn-danger"
                      onClick={() => handleDelete(user)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingUser ? 'Edit User' : 'Add User'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={form.email}
                  onChange={handleEmailChange}
                  required
                  className={emailError ? 'input-error' : ''}
                />
                {emailError && <span className="error-message">{emailError}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="password">
                  Password {editingUser && '(leave empty to keep current)'}
                </label>
                <input
                  type="password"
                  id="password"
                  value={form.password}
                  onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                  {...(!editingUser && { required: true })}
                  minLength={8}
                />
              </div>
              <div className="form-group">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  value={form.role}
                  onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
                >
                  <option value="user">User</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setEditingUser(null);
                    setForm({ email: '', name: '', password: '', role: 'user' });
                    setEmailError('');
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Save Changes' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign to Blog Modal */}
      {showAssignModal && assigningUser && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Assign {assigningUser.name} to Blog</h2>
            <form onSubmit={handleAssignSubmit}>
              <div className="form-group">
                <label htmlFor="blogId">Blog</label>
                <select
                  id="blogId"
                  value={assignForm.blogId}
                  onChange={(e) => setAssignForm(f => ({ ...f, blogId: e.target.value }))}
                  required
                >
                  <option value="">Select a blog</option>
                  {blogs.map(blog => (
                    <option key={blog.id} value={blog.id}>{blog.blog_id} - {blog.domain}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="blogRole">Role in this blog</label>
                <select
                  id="blogRole"
                  value={assignForm.role}
                  onChange={(e) => setAssignForm(f => ({ ...f, role: e.target.value }))}
                >
                  <option value="user">User (can edit)</option>
                  <option value="viewer">Viewer (read-only)</option>
                </select>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAssignModal(false);
                    setAssigningUser(null);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Assign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
